/**
 * src/pages/pipeline/PipelinePanels.tsx
 *
 * Panels for the Pipeline page (team design doc, 2026-09-22):
 *   Jev reads titles, the backup AI only runs when Jev fails, Jev picks the
 *   product from the catalogue (or the deal is not sent), and a small image
 *   model checks the photo.
 *
 * The catalogue panels can edit: add a product by hand, hide one, restore
 * one. Hiding is a flag on the server, so every edit here can be undone.
 */

import { Fragment, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, EyeOff, RotateCcw, Search } from 'lucide-react';
import {
  fetchPipelineStatus, fetchPipelineSummary, fetchCatalogueMisses, fetchCatalogue,
  addCatalogueProduct, setProductHidden, fetchSimilarProducts, fetchBrandSuggestions,
  type PipelineStatus, type PipelineSummary, type MissGroup, type CatalogueRow, type AddProductInput, type TierRow,
} from '@/api/pipeline';
import ChartCard from '@/components/ChartCard';
import { cn } from '@/lib/cn';

const CATEGORIES = [
  'phones', 'laptops', 'consoles', 'audio', 'monitors', 'gpus', 'wearables',
  'tablets', 'cameras', 'tvs', 'pcparts', 'games', 'accessories', 'vr',
];
const SERVED = new Set(['phones', 'laptops', 'consoles', 'audio', 'monitors', 'gpus', 'wearables']);

const REASON_LABEL: Record<string, string> = {
  resolved: 'Named a product',
  not_in_catalogue: 'Jev: none of these products',
  not_a_device: 'Not a device',
  several_models: 'Several models in one listing',
  unknown_brand: 'No brand we carry',
  no_read: 'Title not read yet',
  wrong_category: 'Product filed in another category',
  ambiguous: 'Several models / vague (before 3 Oct)',
  low_probability: 'Not sure which product',
  unknown_choice: 'Picked outside the list',
  no_candidates: 'Nothing similar in catalogue',
  provider_error: 'Jev failed (backup used)',
  disabled: 'Category not covered',
};
const REASON_TONE: Record<string, string> = {
  resolved: 'bg-emerald-500',
  not_in_catalogue: 'bg-amber-500',
  not_a_device: 'bg-slate-500',
  ambiguous: 'bg-sky-600',
  several_models: 'bg-sky-600',
  unknown_brand: 'bg-violet-600',
  low_probability: 'bg-sky-800',
  provider_error: 'bg-red-500',
};

export const HOUR_WINDOWS = [6, 24, 168] as const;

/** Render-safe text: the API should only send strings, but a stray object must not crash the page. */
const str = (v: unknown, fallback = '—') => (typeof v === 'string' && v ? v : typeof v === 'number' ? String(v) : fallback);
const n0 = (n: number | null | undefined) => (n == null || !Number.isFinite(n) ? '—' : n.toLocaleString());
const pct = (a: number, b: number) => (b > 0 ? `${((a / b) * 100).toFixed(0)}%` : '—');

export function Stat({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: 'good' | 'warn' | 'bad' }) {
  return (
    <div className="rounded border border-slate-800 bg-slate-950/40 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={cn(
        'mt-0.5 break-words text-lg font-semibold tabular-nums',
        tone === 'good' && 'text-emerald-400', tone === 'warn' && 'text-amber-400', tone === 'bad' && 'text-red-400',
        !tone && 'text-slate-100',
      )}>{value}</div>
      {hint && <div className="mt-0.5 break-words text-[11px] leading-tight text-slate-500">{hint}</div>}
    </div>
  );
}

// ── Status ────────────────────────────────────────────────────────────────

export function StatusStrip() {
  const q = useQuery<PipelineStatus>({ queryKey: ['pipeline', 'status'], queryFn: fetchPipelineStatus, refetchInterval: 30_000 });
  const s = q.data;
  const cats = s ? Object.entries(s.catalogue.byCategory).filter(([c]) => SERVED.has(c)).sort((a, b) => b[1] - a[1]) : [];
  return (
    <ChartCard title="Status" subtitle="Live, from the running process" isLoading={q.isLoading} isError={q.isError}>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        <Stat label="Jev (reads titles)" value={s ? (s.jev.enabled ? 'On' : 'OFF') : '—'}
              tone={s ? (s.jev.enabled ? 'good' : 'bad') : undefined}
              hint={s?.jev.enabled ? s.jev.model ?? undefined : 'no key, or JEV_ENABLED=0 — backup AI is doing everything'} />
        <Stat label="Jev breaker" value={s ? (s.jev.breakerOpen ? 'OPEN' : 'Closed') : '—'}
              tone={s?.jev.breakerOpen ? 'bad' : undefined}
              hint={s?.jev.breakerOpen
                ? `backup AI until ${new Date(s.jev.openUntil!).toLocaleTimeString()}`
                : s ? `${s.jev.consecutiveFailures} failures in a row (opens at 5)` : undefined} />
        <Stat label="Backup AI" value={s?.backup.tierPin ? `tier ${s.backup.tierPin}` : 'auto'}
              hint={s?.backup.models.join(' · ') || undefined} />
        <Stat label="Photo check" value={s ? (s.photos.reachable ? 'Up' : 'DOWN') : '—'}
              tone={s ? (s.photos.reachable ? 'good' : 'bad') : undefined}
              hint={s?.photos.head ?? (s?.photos.url ? 'not answering: photos stored as unknown' : 'IMAGE_CLASSIFIER_URL=off')} />
        <Stat label="Catalogue" value={s ? n0(s.catalogue.products) : '—'}
              hint={s ? `products Jev can choose from · parser v${s.catalogue.parserVersion} · ${s.catalogue.manual} added by hand · ${s.catalogue.hidden} hidden` : undefined} />
      </div>
      {cats.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          {cats.map(([c, n]) => (
            <span key={c} className="rounded border border-slate-800 bg-slate-950/60 px-2 py-0.5 text-slate-300">{c}: <b>{n0(n)}</b></span>
          ))}
        </div>
      )}
    </ChartCard>
  );
}

export function WindowToggle({ hours, onChange }: { hours: number; onChange: (h: number) => void }) {
  return (
    <div className="flex items-center gap-1">
      {HOUR_WINDOWS.map(h => (
        <button key={h} onClick={() => onChange(h)}
                className={cn('rounded px-2 py-1 text-xs', hours === h ? 'bg-slate-700 text-slate-100' : 'bg-slate-900 text-slate-400 hover:bg-slate-800')}>
          {h === 168 ? '7d' : `${h}h`}
        </button>
      ))}
    </div>
  );
}

// ── Summary panels ───────────────────────────────────────────────────────

function TierLine({ name, t, tone }: { name: string; t: TierRow; tone?: 'warn' }) {
  return (
    <tr className="border-b border-slate-900">
      <td className={cn('py-1', tone === 'warn' && 'text-amber-300')}>{name}</td>
      <td className="py-1 text-right tabular-nums">{n0(t.calls)}</td>
      <td className="py-1 text-right tabular-nums">{n0(t.ok)}</td>
      <td className={cn('py-1 text-right tabular-nums', t.failed > 0 && 'text-amber-400')}>{n0(t.failed)}</td>
      <td className="py-1 text-right tabular-nums">{n0(t.avgLatencyMs)} ms</td>
      <td className="py-1 text-right tabular-nums">£{t.costGbp.toFixed(4)}</td>
    </tr>
  );
}

export function SummaryPanels({ hours }: { hours: number }) {
  const q = useQuery<PipelineSummary>({ queryKey: ['pipeline', 'summary', hours], queryFn: () => fetchPipelineSummary(hours), refetchInterval: 60_000 });
  const s = q.data;
  const reasons = s ? Object.entries(s.identity.byReason).sort((a, b) => b[1] - a[1]) : [];
  const cats = s ? Object.entries(s.identity.byCategory).sort((a, b) =>
    Object.values(b[1]).reduce((x, y) => x + y, 0) - Object.values(a[1]).reduce((x, y) => x + y, 0)) : [];
  const own = s?.titles.own ?? { calls: 0, ok: 0, failed: 0, avgLatencyMs: 0, costGbp: 0 };
  const allTitles = s ? s.titles.jev.calls + own.calls + s.titles.backup.calls : 0;

  return (
    <>
      <ChartCard title="1. Reading titles" subtitle="Per the reader setting (Title reader page). Our models read whenever Jev cannot; the backup AI only if they cannot either."
                 isLoading={q.isLoading} isError={q.isError} isEmpty={!!s && allTitles === 0} emptyLabel="No titles read in this window.">
        {s && (
          <>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              <Stat label="Read by Jev" value={pct(s.titles.jev.calls, allTitles)} tone="good" hint={`${n0(s.titles.jev.calls)} titles`} />
              <Stat label="Our models" value={pct(own.calls, allTitles)} hint={`${n0(own.calls)} titles, £0`} />
              <Stat label="Backup AI" value={pct(s.titles.backup.calls, allTitles)}
                    tone={s.titles.backup.calls > allTitles * 0.05 ? 'warn' : undefined} hint={`${n0(s.titles.backup.calls)} titles`} />
              <Stat label="Jev failures" value={n0(s.titles.jev.failed)} tone={s.titles.jev.failed > 0 ? 'warn' : undefined} />
              <Stat label="Cost" value={`£${(s.titles.jev.costGbp + s.titles.backup.costGbp).toFixed(3)}`} hint="Jev + backup, this window" />
            </div>
            <table className="mt-3 w-full text-xs">
              <thead className="text-slate-500"><tr className="border-b border-slate-800">
                <th className="py-1 text-left font-medium">Who</th><th className="py-1 text-right font-medium">Calls</th>
                <th className="py-1 text-right font-medium">OK</th><th className="py-1 text-right font-medium">Failed</th>
                <th className="py-1 text-right font-medium">Avg</th><th className="py-1 text-right font-medium">Cost</th>
              </tr></thead>
              <tbody className="text-slate-300">
                <TierLine name="Jev" t={s.titles.jev} />
                {own.calls > 0 && <TierLine name="our models" t={own} />}
                {Object.entries(s.titles.backupByModel).sort((a, b) => b[1].calls - a[1].calls).map(([m, t]) =>
                  <TierLine key={m} name={`backup · ${m}`} t={t} tone="warn" />)}
              </tbody>
            </table>
          </>
        )}
      </ChartCard>

      <ChartCard title="2. Naming the product" subtitle="Jev picks from the catalogue, or the deal is not sent. One decision per listing, cached."
                 isLoading={q.isLoading} isError={q.isError} isEmpty={!!s && s.identity.total === 0} emptyLabel="No decisions in this window.">
        {s && s.identity.total > 0 && (
          <>
            <div className="flex h-3 w-full overflow-hidden rounded bg-slate-900">
              {reasons.map(([r, n]) => (
                <div key={r} title={`${REASON_LABEL[r] ?? r}: ${n}`} className={REASON_TONE[r] ?? 'bg-slate-700'}
                     style={{ width: `${(n / s.identity.total) * 100}%` }} />
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-300">
              {reasons.map(([r, n]) => (
                <span key={r} className="inline-flex items-center gap-1">
                  <span className={cn('inline-block h-2 w-2 rounded-sm', REASON_TONE[r] ?? 'bg-slate-700')} />
                  {REASON_LABEL[r] ?? r}: <b>{n0(n)}</b> ({pct(n, s.identity.total)})
                </span>
              ))}
            </div>
            <table className="mt-3 w-full text-xs">
              <thead className="text-slate-500"><tr className="border-b border-slate-800">
                <th className="py-1 text-left font-medium">Category</th>
                <th className="py-1 text-right font-medium">Named</th>
                <th className="py-1 text-right font-medium">Not in catalogue</th>
                <th className="py-1 text-right font-medium">Not a device</th>
                <th className="py-1 text-right font-medium">Other</th>
                <th className="py-1 text-right font-medium">Named %</th>
              </tr></thead>
              <tbody className="text-slate-300">
                {cats.map(([c, m]) => {
                  const total = Object.values(m).reduce((x, y) => x + y, 0);
                  const other = total - (m.resolved ?? 0) - (m.not_in_catalogue ?? 0) - (m.not_a_device ?? 0);
                  return (
                    <tr key={c} className="border-b border-slate-900">
                      <td className="py-1">{c}</td>
                      <td className="py-1 text-right tabular-nums text-emerald-300">{n0(m.resolved ?? 0)}</td>
                      <td className="py-1 text-right tabular-nums text-amber-300">{n0(m.not_in_catalogue ?? 0)}</td>
                      <td className="py-1 text-right tabular-nums">{n0(m.not_a_device ?? 0)}</td>
                      <td className="py-1 text-right tabular-nums">{n0(other)}</td>
                      <td className="py-1 text-right tabular-nums">{pct(m.resolved ?? 0, total - (m.not_a_device ?? 0))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="mt-2 text-[11px] text-slate-500">Named % leaves out listings that are not devices, which are correctly dropped.</p>
          </>
        )}
      </ChartCard>

      <ChartCard title="3. Checking the photo" subtitle="Our image model. Only a sure &quot;not the product&quot; stops a deal; unsure is stored as unknown."
                 isLoading={q.isLoading} isError={q.isError}>
        {s && (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label="Judged" value={n0(s.photos.judged)} />
            <Stat label="The product" value={n0(s.photos.usable)} tone="good" />
            <Stat label="Not the product" value={n0(s.photos.notProduct)} tone={s.photos.notProduct ? 'warn' : undefined} hint="deal dropped" />
            <Stat label="Unsure" value={n0(s.photos.unsure)} hint="sent without judgement" />
            <Stat label="Could not ask" value={n0(s.photos.couldNotAsk)} tone={s.photos.couldNotAsk ? 'warn' : undefined} hint="classifier down / timeout" />
            <Stat label="No photo fetched" value={n0(s.photos.noPhoto)} hint="e.g. CeX blocks our server" />
          </div>
        )}
      </ChartCard>

      <ChartCard title="4. What goes out" subtitle="Only deals scored by the new pipeline can be served." isLoading={q.isLoading} isError={q.isError}>
        {s && (
          <>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              <Stat label="Servable now (EMIT)" value={n0(Object.values(s.served.emitServableNow).reduce((a, b) => a + b, 0))} />
              <Stat label="Pushes sent" value={n0(s.served.pushes)} hint="this window" />
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              {Object.entries(s.served.emitServableNow).sort((a, b) => b[1] - a[1]).map(([c, n]) => (
                <span key={c} className="rounded border border-slate-800 bg-slate-950/60 px-2 py-0.5 text-slate-300">
                  {c}: <b>{n}</b> servable · {s.served.pushesByCategory[c] ?? 0} pushed
                </span>
              ))}
            </div>
          </>
        )}
      </ChartCard>
    </>
  );
}

// ── Catalogue: brand suggestions ─────────────────────────────────────────

export function BrandSuggestionsPanel({ hours }: { hours: number }) {
  const q = useQuery({ queryKey: ['pipeline', 'brand-suggestions', hours], queryFn: () => fetchBrandSuggestions(Math.max(hours, 168)), refetchInterval: 300_000 });
  const d = q.data;
  return (
    <ChartCard title="Brands we don't carry"
               subtitle="Makes the backup AI named for listings, that our catalogue has no products for. A suggestion only: classification still picks from our own brands. Last 7 days."
               isLoading={q.isLoading} isError={q.isError} isEmpty={!!d && d.suggestions.length === 0} emptyLabel="No suggestions in this window.">
      {d && (
        <div className="space-y-2 text-xs">
          <div className="text-slate-400">
            Catalogue products with no brand yet: <b className={d.productsWithoutBrand ? 'text-amber-300' : 'text-slate-100'}>{d.productsWithoutBrand}</b>
            <span className="text-slate-500"> (offered under every brand until filled)</span>
          </div>
          <table className="w-full">
            <thead className="text-slate-500"><tr className="border-b border-slate-800">
              <th className="py-1 text-left font-medium">Brand</th><th className="py-1 text-right font-medium">Listings</th><th className="py-1 pl-3 text-left font-medium">Examples</th>
            </tr></thead>
            <tbody className="text-slate-300">
              {d.suggestions.map(s => (
                <tr key={s.brand} className="border-b border-slate-900 align-top">
                  <td className="py-1 font-medium">{s.brand}</td>
                  <td className="py-1 text-right">{s.listings}</td>
                  <td className="py-1 pl-3 text-slate-400">{s.examples.join(' · ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ChartCard>
  );
}

// ── Catalogue: misses and management ──────────────────────────────────────

export function MissesPanel({ hours, onAdd }: { hours: number; onAdd: (seed: Partial<AddProductInput>) => void }) {
  const q = useQuery<MissGroup[]>({ queryKey: ['pipeline', 'misses', hours], queryFn: () => fetchCatalogueMisses(hours), refetchInterval: 120_000 });
  const [open, setOpen] = useState<string | null>(null);
  return (
    <ChartCard title="Real devices not in the catalogue" subtitle="These were not sent. Grouped by the old keyword key, which only labels the group. Add the product if we should carry it."
               isLoading={q.isLoading} isError={q.isError} isEmpty={!!q.data && q.data.length === 0} emptyLabel="Nothing missed in this window.">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-slate-500"><tr className="border-b border-slate-800">
            <th className="py-1 text-left font-medium">Group</th><th className="py-1 text-left font-medium">Category</th>
            <th className="py-1 text-right font-medium">Listings</th><th className="py-1 text-left font-medium pl-3">Example</th><th />
          </tr></thead>
          <tbody className="text-slate-300">
            {q.data?.map(m => (
              <Fragment key={m.key}>
                <tr className="border-b border-slate-900 align-top">
                  <td className="py-1 font-mono">
                    <button className="text-left hover:text-slate-100" onClick={() => setOpen(open === m.key ? null : m.key)}>{m.key}</button>
                  </td>
                  <td className="py-1">{str(m.category)}</td>
                  <td className="py-1 text-right tabular-nums">{m.listings}</td>
                  <td className="py-1 pl-3 text-slate-400">{str(m.examples[0]?.title, '')}</td>
                  <td className="py-1 text-right">
                    <button onClick={() => onAdd({ category: m.category ?? '', fromListing: m.examples[0]?.title ?? '' })}
                            className="inline-flex items-center gap-1 rounded bg-slate-800 px-2 py-0.5 text-slate-200 hover:bg-slate-700">
                      <Plus className="h-3 w-3" />Add
                    </button>
                  </td>
                </tr>
                {open === m.key && m.examples.map((e, i) => (
                  <tr key={`${m.key}-${i}`} className="border-b border-slate-900 text-slate-500">
                    <td /><td className="py-0.5">{str(e.source, '')}</td><td className="py-0.5 text-right">{typeof e.price === 'number' ? `£${e.price}` : ''}</td>
                    <td className="py-0.5 pl-3" colSpan={2}>{str(e.title, '')}</td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </ChartCard>
  );
}

export function AddProductForm({ seed, onDone }: { seed: Partial<AddProductInput>; onDone: () => void }) {
  const qc = useQueryClient();
  // The name starts empty on purpose: a listing title is not a product name.
  // On 2026-09-27 an add pre-filled with the listing's title stored
  // "... Space Black RP£3299" as a product, duplicating one CeX already had.
  const [f, setF] = useState<AddProductInput>({
    displayName: seed.displayName ?? '', category: seed.category ?? 'phones', brand: seed.brand ?? '',
    modelLine: seed.modelLine ?? '', storageGb: seed.storageGb ?? null, ramGb: seed.ramGb ?? null,
  });
  const lookup = (f.displayName.trim() || seed.fromListing || '').trim();
  const similar = useQuery({
    queryKey: ['catalogue-similar', lookup, f.category],
    queryFn: () => fetchSimilarProducts(lookup, f.category),
    enabled: lookup.length >= 3,
    staleTime: 30_000,
  });
  const m = useMutation({
    mutationFn: () => addCatalogueProduct({
      ...f, fromListing: undefined, brand: f.brand || null,
      storageGb: f.storageGb ? Number(f.storageGb) : null,
      ramGb: f.category === 'laptops' && f.ramGb ? Number(f.ramGb) : null,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pipeline'] }); onDone(); },
  });
  const input = 'w-full rounded border border-slate-800 bg-slate-950 px-2 py-1 text-xs text-slate-100';
  return (
    <div className="rounded border border-slate-700 bg-slate-900/60 p-3">
      <div className="mb-2 text-xs font-medium text-slate-200">Add a product to the catalogue</div>
      <p className="mb-3 text-[11px] text-slate-500">
        Write the name the way a retailer would, specific enough that Jev can tell it from its siblings
        (generation, size, storage). No colour, condition, seller words or price. One product per add:
        a listing that says "128GB 256GB" is two products. Products added here are never overwritten by the CeX seeder.
      </p>
      {seed.fromListing && (
        <div className="mb-2 rounded bg-slate-950/70 px-2 py-1 text-[11px] text-slate-400">
          From the listing: <span className="text-slate-300">{seed.fromListing}</span>
        </div>
      )}
      {similar.data && similar.data.length > 0 && (
        <div className="mb-3 rounded border border-amber-900/50 bg-amber-950/20 px-2 py-1.5 text-[11px]">
          <div className="text-amber-300">Already in the catalogue: check it is not one of these before adding</div>
          <ul className="mt-1 space-y-0.5 text-slate-300">
            {similar.data.map(p => <li key={p.key}>{p.displayName} <span className="font-mono text-slate-600">{p.key}</span></li>)}
          </ul>
        </div>
      )}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-6">
        <label className="sm:col-span-3 text-[11px] text-slate-400">Name shown to Jev and on cards
          <input className={input} value={f.displayName} onChange={e => setF({ ...f, displayName: e.target.value })}
                 placeholder={f.category === 'laptops' ? 'Apple MacBook Air 13-inch M2 (8-CPU 8-GPU), 8GB RAM' : 'Samsung Galaxy A55 5G 128GB'} />
        </label>
        <label className="text-[11px] text-slate-400">Category
          <select className={input} value={f.category} onChange={e => setF({ ...f, category: e.target.value })}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="text-[11px] text-slate-400">Brand
          <input className={input} value={f.brand ?? ''} onChange={e => setF({ ...f, brand: e.target.value })} placeholder="Apple" />
        </label>
        <label className="text-[11px] text-slate-400">Model line (for the key)
          <input className={input} value={f.modelLine} onChange={e => setF({ ...f, modelLine: e.target.value })} placeholder="MacBook Air 13in M2" />
        </label>
        <label className="text-[11px] text-slate-400">Storage GB
          <input className={input} type="number" value={f.storageGb ?? ''}
                 onChange={e => setF({ ...f, storageGb: e.target.value === '' ? null : Number(e.target.value) })} />
        </label>
        <label className="text-[11px] text-slate-400">RAM GB (laptops only)
          <input className={input} type="number" disabled={f.category !== 'laptops'} value={f.category === 'laptops' ? (f.ramGb ?? '') : ''}
                 onChange={e => setF({ ...f, ramGb: e.target.value === '' ? null : Number(e.target.value) })} />
        </label>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button disabled={m.isPending || !f.displayName.trim() || !f.modelLine.trim()} onClick={() => m.mutate()}
                className="rounded bg-emerald-700 px-3 py-1 text-xs text-white hover:bg-emerald-600 disabled:opacity-40">
          {m.isPending ? 'Adding…' : 'Add product'}
        </button>
        <button onClick={onDone} className="rounded bg-slate-800 px-3 py-1 text-xs text-slate-300 hover:bg-slate-700">Cancel</button>
        {m.isError && <span className="text-xs text-red-400">{String((m.error as any)?.body?.error ?? (m.error as Error).message)}</span>}
      </div>
    </div>
  );
}

export function CataloguePanel({ addSeed, setAddSeed }: {
  addSeed: Partial<AddProductInput> | null; setAddSeed: (s: Partial<AddProductInput> | null) => void;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [source, setSource] = useState('');
  const [hidden, setHidden] = useState(false);
  const q = useQuery({ queryKey: ['pipeline', 'catalogue', query, category, source, hidden],
    queryFn: () => fetchCatalogue({ q: query, category, source, hidden }) });
  const toggle = useMutation({
    mutationFn: (r: CatalogueRow) => setProductHidden(r.key, !r.hidden),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipeline'] }),
  });
  const sel = 'rounded border border-slate-800 bg-slate-950 px-2 py-1 text-xs text-slate-200';

  return (
    <ChartCard title="Catalogue" subtitle="What Jev can choose from. Removing a product hides it (reversible); the CeX seeder will not bring it back."
               actions={!addSeed && (
                 <button onClick={() => setAddSeed({})} className="inline-flex items-center gap-1 rounded bg-emerald-800 px-2 py-1 text-xs text-white hover:bg-emerald-700">
                   <Plus className="h-3 w-3" />Add product
                 </button>
               )}
               isLoading={q.isLoading} isError={q.isError}>
      {addSeed && <div className="mb-3"><AddProductForm key={JSON.stringify(addSeed)} seed={addSeed} onDone={() => setAddSeed(null)} /></div>}
      <form className="flex flex-wrap items-center gap-2" onSubmit={e => { e.preventDefault(); setQuery(text.trim()); }}>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1.5 h-3.5 w-3.5 text-slate-500" />
          <input value={text} onChange={e => setText(e.target.value)} placeholder="Search: air m2 512"
                 className="w-64 rounded border border-slate-800 bg-slate-950 py-1 pl-7 pr-2 text-xs text-slate-100" />
        </div>
        <select className={sel} value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">All categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className={sel} value={source} onChange={e => setSource(e.target.value)}>
          <option value="">All sources</option><option value="cex">CeX</option><option value="manual">Added by hand</option>
        </select>
        <label className="inline-flex items-center gap-1 text-xs text-slate-400">
          <input type="checkbox" checked={hidden} onChange={e => setHidden(e.target.checked)} />Show removed
        </label>
        <button type="submit" className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700">Search</button>
        {q.data && <span className="text-xs text-slate-500">{q.data.total.toLocaleString()} match{q.data.total === 1 ? '' : 'es'}{q.data.total > q.data.rows.length ? ` · showing ${q.data.rows.length}` : ''}</span>}
      </form>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-slate-500"><tr className="border-b border-slate-800">
            <th className="py-1 text-left font-medium">Product</th><th className="py-1 text-left font-medium">Category</th>
            <th className="py-1 text-left font-medium">Source</th><th className="py-1 text-left font-medium">Last seen</th><th />
          </tr></thead>
          <tbody className="text-slate-300">
            {q.data?.rows.map(r => (
              <tr key={r.key} className={cn('border-b border-slate-900', r.hidden && 'opacity-60')}>
                <td className="py-1">
                  <div>{str(r.displayName)}</div>
                  <div className="font-mono text-[10px] text-slate-600">{str(r.key)}</div>
                </td>
                <td className="py-1">{str(r.category)}</td>
                <td className="py-1">{r.source === 'manual' ? 'by hand' : r.source === 'cex' ? `CeX (${r.boxes})` : r.source}</td>
                <td className="py-1 text-slate-500">{r.lastSeen ? new Date(r.lastSeen).toLocaleDateString() : '—'}</td>
                <td className="py-1 text-right">
                  <button disabled={toggle.isPending} onClick={() => toggle.mutate(r)}
                          className="inline-flex items-center gap-1 rounded bg-slate-800 px-2 py-0.5 text-slate-200 hover:bg-slate-700 disabled:opacity-40">
                    {r.hidden ? <><RotateCcw className="h-3 w-3" />Restore</> : <><EyeOff className="h-3 w-3" />Remove</>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {toggle.isError && <p className="mt-2 text-xs text-red-400">{(toggle.error as Error).message}</p>}
    </ChartCard>
  );
}
